<script setup lang="ts">
/**
 * Loading placeholder with the AI-button shimmer (FE-005).
 *
 * `media` skeletons carry a small static FR monogram in the middle — the same
 * mark as the page loader, but deliberately motionless: only the shimmer moves.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  normalizeFairSkeletonVariant,
  resolveFairSkeletonMarkSize,
  toFairSkeletonLength,
  FAIR_SKELETON_TEXT_HEIGHT,
  type FairSkeletonVariant,
} from '@/ui/tokens/fair-skeleton.data'
import { FAIR_PAGE_LOADER_MONOGRAM_PATH } from '@/ui/tokens/fair-page-loader.data'

interface FairSkeletonProps {
  variant?: FairSkeletonVariant
  width?: number | string
  height?: number | string
  /** Cover a `position: relative` parent instead of taking part in layout. */
  fill?: boolean
  /** Show the FR mark (default: only on `media`). */
  mark?: boolean
  radius?: string
  /** Announced label; omitted keeps the node out of the a11y tree. */
  label?: string
}

const props = withDefaults(defineProps<FairSkeletonProps>(), {
  variant: 'text',
  width: undefined,
  height: undefined,
  fill: false,
  mark: undefined,
  radius: undefined,
  label: undefined,
})

const rootRef = ref<HTMLElement | null>(null)
const markSize = ref(0)

const resolvedVariant = computed(() => normalizeFairSkeletonVariant(props.variant))

const showMark = computed(
  () => props.mark ?? resolvedVariant.value === 'media',
)

const rootClass = computed(() => [
  'fair-skeleton',
  `fair-skeleton--${resolvedVariant.value}`,
  { 'fair-skeleton--fill': props.fill },
])

const rootStyle = computed(() => {
  const height =
    props.height ??
    (resolvedVariant.value === 'text' ? FAIR_SKELETON_TEXT_HEIGHT : undefined)

  return {
    width: toFairSkeletonLength(props.width),
    height: toFairSkeletonLength(height),
    borderRadius: props.radius,
  }
})

/** The mark scales with the box, so one primitive covers thumbnails and heroes. */
function measureMark() {
  const el = rootRef.value
  if (!el || !showMark.value) return
  const { width, height } = el.getBoundingClientRect()
  const shorter = Math.min(width, height)
  markSize.value = shorter > 0 ? resolveFairSkeletonMarkSize(shorter) : 0
}

let observer: ResizeObserver | null = null

onMounted(() => {
  measureMark()
  if (typeof ResizeObserver === 'undefined' || !rootRef.value) return
  observer = new ResizeObserver(measureMark)
  observer.observe(rootRef.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <span
    ref="rootRef"
    :class="rootClass"
    :style="rootStyle"
    :role="label ? 'status' : undefined"
    :aria-label="label"
    :aria-hidden="label ? undefined : 'true'"
  >
    <svg
      v-if="showMark && markSize > 0"
      class="fair-skeleton__mark"
      :width="markSize"
      :height="markSize"
      viewBox="0 0 57.2887 57.2887"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path :d="FAIR_PAGE_LOADER_MONOGRAM_PATH" />
    </svg>
  </span>
</template>
